import React, { useState, useEffect } from 'react';
import { Lock, Check, AlertCircle, ArrowRight, Home, RefreshCw } from 'lucide-react';
import { useParams, useNavigate, useLocation  } from "react-router-dom";

// Password Reset Form Component
const PasswordResetForm = () => {
  // Form states
  const { token } = useParams(); 
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countdown, setCountdown] = useState(7);
  const [errors, setErrors] = useState({});
  const [strength, setStrength] = useState(0);
  const [redirectUrl, setRedirectUrl] = useState(''); 


  // Password strength criteria
  const calculateStrength = (pwd) => {
      let score = 0;
      if (pwd.length >= 8) score += 1;
      if (/[A-Z]/.test(pwd)) score += 1;
      if (/[a-z]/.test(pwd)) score += 1;
      if (/[0-9]/.test(pwd)) score += 1;
      if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
      setStrength(score);
  };

  // Password validation
  const validatePassword = () => {
      const newErrors = {};

      if (!password) {
          newErrors.password = 'Password is required';
      } else if (password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
      }

      if (!confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
      } else if (password !== confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
      }

      setErrors(newErrors);
      setTimeout(() => {
        setErrors({});
      }, 5000);
      return Object.keys(newErrors).length === 0;
  };

  // Handle password change
  const handlePasswordChange = (e) => {
      const newPassword = e.target.value;
      setPassword(newPassword);
      calculateStrength(newPassword);
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
      setConfirmPassword(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (validatePassword()) {
      setIsSubmitting(true);
  
      const searchParams = new URLSearchParams(location.search);
        const redirectUri = searchParams.get('redirectUrl');

        const resetUrl = `/auth/reset-pwd/${token}?redirectUrl=${encodeURIComponent(redirectUri)}`;

  
      try {
        const response = await fetch(resetUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ new_password: password }), // Sending new password
        });
  
        const data = await response.json();
  
        if (response.ok) {
          setIsSuccess(true); // Set success state to true
          setRedirectUrl(data.redirectUrl || '/'); // Set redirect URL from the response or default to home
          setPassword(''); // Clear password fields if needed
          setConfirmPassword('');
          setErrors({});
        } else {
          setErrors({ submit: data.error || 'Failed to reset password' });
          setPassword(''); // Clear password fields if needed
          setConfirmPassword('');
          setTimeout(() => {
            setErrors({});
          }, 5000);
        }
      } catch (err) {
        setErrors({ submit: 'An error occurred while resetting the password' });
        setTimeout(() => {
          setErrors({});
        }, 5000);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  

  // Auto redirect countdown
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        if (countdown > 0) {
          setCountdown(countdown - 1);
        } else {
          // Redirect to the specified URL or default to home
          window.location.href = redirectUrl || '/login'; 
        }
      }, 1000); // Decrease the countdown every second
      return () => clearTimeout(timer); // Cleanup on unmount
    }
  }, [isSuccess, countdown, redirectUrl]); // Add redirectUrl to dependency array
  
  // Render success screen
  if (isSuccess) {
    return (
      <div className="flex flex-col items-center text-center p-8 max-w-md mx-auto animate-fade-in">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6 animate-bounce">
          <Check className="text-white w-8 h-8" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Password Reset Successful</h1>
        <p className="text-gray-400 mb-6">Your password has been securely updated.</p>
        
        <div className="w-full bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Auto-redirecting in</span>
            <span className="font-mono text-purple-400 font-bold">{countdown}s</span>
          </div>
          <div className="w-full bg-gray-700 h-2 rounded-full mt-2 overflow-hidden">
            <div 
              className="bg-purple-500 h-full rounded-full transition-all duration-1000 ease-linear"
              style={{ width: `${(countdown / 7) * 100}%` }}
            />
          </div>
        </div>
        
        <a 
          href={redirectUrl || '/login'} 
          className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-lg transition-colors duration-300"
        >
          <Home size={18} />
          <span>Return to Homepage</span>
        </a>
      </div>
    );
  }
  
  // Render the password reset form
  return (
    <div className="max-w-md mx-auto p-6 animate-slide-up">
      <div className="text-center mb-8">
        <div className="inline-flex justify-center items-center w-16 h-16 bg-purple-600 bg-opacity-20 rounded-full mb-4">
          <Lock className="w-8 h-8 text-purple-500" />
        </div>
        <h1 className="text-2xl font-bold text-white">Reset Your Password</h1>
        <p className="text-gray-400 mt-2">Create a strong new password for your 0x10LayerFF account</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={handlePasswordChange}
              className={`w-full bg-gray-800 border ${errors.password ? 'border-red-500' : 'border-gray-700'} rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300`}
              placeholder="Enter new password"
            />
            {errors.password && (
              <div className="absolute right-3 top-3 text-red-500">
                <AlertCircle size={18} />
              </div>
            )}
          </div>
          {errors.password && (
            <p className="mt-2 text-sm text-red-500">{errors.password}</p>
          )}
          
          {/* Password strength meter */}
          {password.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-400">Password Strength</span>
                <span className="text-xs font-medium" style={{ 
                  color: strength < 2 ? '#ef4444' : strength < 4 ? '#f59e0b' : '#10b981' 
                }}>
                  {strength < 2 ? 'Weak' : strength < 4 ? 'Medium' : 'Strong'}
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full transition-all duration-300"
                  style={{ 
                    width: `${strength * 20}%`,
                    backgroundColor: strength < 2 ? '#ef4444' : strength < 4 ? '#f59e0b' : '#10b981' 
                  }}
                />
              </div>
              
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className={`flex items-center gap-1 ${password.length >= 8 ? 'text-green-500' : 'text-gray-500'}`}>
                  <Check size={12} />
                  <span>8+ characters</span>
                </div>
                <div className={`flex items-center gap-1 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-gray-500'}`}>
                  <Check size={12} />
                  <span>Uppercase</span>
                </div>
                <div className={`flex items-center gap-1 ${/[0-9]/.test(password) ? 'text-green-500' : 'text-gray-500'}`}>
                  <Check size={12} />
                  <span>Numbers</span>
                </div>
                <div className={`flex items-center gap-1 ${/[^A-Za-z0-9]/.test(password) ? 'text-green-500' : 'text-gray-500'}`}>
                  <Check size={12} />
                  <span>Special chars</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full bg-gray-800 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-700'} rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300`}
              placeholder="Confirm your password"
            />
            {errors.confirmPassword && (
              <div className="absolute right-3 top-3 text-red-500">
                <AlertCircle size={18} />
              </div>
            )}
          </div>
          {errors.confirmPassword && (
            <p className="mt-2 text-sm text-red-500">{errors.confirmPassword}</p>
          )}
        </div>
        {errors.submit && <p style={{ color: 'red' }}>{errors.submit}</p>}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-3 px-6 rounded-lg transition-all duration-300 ${isSubmitting ? 'opacity-70' : ''}`}
        >
          {isSubmitting ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Reset Password</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// App wrapper with dark theme and animation styles
const PasswordResetApp = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-500">
            0x10LayerFF
          </h1>
          <p className="text-xs uppercase tracking-wider text-gray-400 mt-1">Security Protocol</p>
        </div>
        
        <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden">
          <PasswordResetForm />
        </div>
        
        <p className="text-center text-xs text-gray-500 mt-8">
          © 2025 0x10LayerFF • Secured Connection
        </p>
      </div>
    </div>
  );
};

export default PasswordResetApp;